import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import invariant from "tiny-invariant"

import { useAgentState } from "@/agent-state"
import {
  toggleConnectAgentStatus,
  useDisplayAgentStatus,
} from "@/agent-state/agent-status-hooks"
import { CUSTOM_PSEUDO_ACW_STATUS } from "@/agent-state/connect-listeners"
import { useCallReportValidationError } from "@/components/ACWForm/report-error-card"
import * as callViewValue from "@/components/CallView/value"
import { useConnectActionsHook } from "@/hooks/connectActions"
import { useContactDataHook } from "@/hooks/contactData"
import { useToast } from "@/hooks/toastHook"
import { useCallPanelLogger } from "@/hooks/useLogger"
import { useSendCallReport } from "@/hooks/useSendCallReport"
import * as connectPanelValue from "@/pages/CustomConnectPanel/value"
import { contactStorageService } from "@/services/localStorageService"

interface ACWButtonsHookInput {
  currentCase: connectPanelValue.Decoder.CaseAndActivityFeeds | null
  isInboundCall: boolean
}

interface ACWButtonsHookType {
  isError?: boolean
  isLoading: boolean
  isPseudoACW: boolean
  isVoiceContact: boolean
  onConfirmAndGoNext: (callAttempted: boolean) => Promise<void>
  onConfirmAndGoOffline: (callAttempted: boolean) => Promise<void>
}

const useACWButtonsHook = ({
  currentCase,
  isInboundCall,
}: ACWButtonsHookInput): ACWButtonsHookType => {
  const { agentRef, send, state } = useAgentState()
  const isPseudoACW = state.matches({ busy: { after_call: "pseudo_acw" } })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { pushCallReportError, resetCallReportErrors } =
    useCallReportValidationError()
  const log = useCallPanelLogger()
  const { error: toastError } = useToast()
  const { setAgentStatus } = useDisplayAgentStatus()
  const { t } = useTranslation()

  const { closeContact, endTask } = useConnectActionsHook()
  const cleanUp = useCleanUp()

  const { hasTask, isVoiceContact } = useContactDataHook()
  const sendCallReportMutation = useSendCallReport({
    currentCase,
    isInboundCall,
  })

  const sendCallReport = async (
    validData: callViewValue.Decoder.DecoderType,
    callAttempted: boolean,
  ) => {
    try {
      await sendCallReportMutation.mutateAsync({
        formData: validData,
        callReportType: callAttempted ? "attempted-call" : "automatic-pre-call",
      })

      return true
    } catch {
      return false
    }
  }

  const cleanupPersistedState = () => {
    try {
      // Cleanup the contact state in local storage after the call report was posted
      cleanUp()
    } catch (err) {
      if (err instanceof Error) {
        log.error(
          `Voice call ACW: Failed to clean up manual call state: ${err.message}`,
        )
      }
    }
  }

  // Send call report and move to the next contact in the queue
  const submitReportAndEndTask = async (callAttempted = true) => {
    setIsLoading(() => true)

    const rawSavedReport = contactStorageService.getCallReport(true)
    const validData = callViewValue.Decoder.schema.safeParse(rawSavedReport)

    if (!validData.success) {
      pushCallReportError(validData.error)
      setIsLoading(() => false)

      return
    }

    if (!(await sendCallReport(validData.data, callAttempted))) {
      setIsLoading(false)

      return // nothing to do if the call report submission failed
    }
    resetCallReportErrors()
    cleanupPersistedState()

    try {
      if (isVoiceContact) {
        await closeContact()
      }
    } catch (err) {
      if (err instanceof Error) {
        log.error(`Voice call ACW: Failed to close contact: ${err.message}`)
      }

      toastError(t("errors.callPanel.nextContactError"))
    }

    try {
      if (hasTask) {
        await endTask()
      }
    } catch (err) {
      if (err instanceof Error) {
        log.error(
          `Voice call ACW: Failed to move to the next task due to error: ${err.message}`,
        )
      }

      toastError(t("errors.callPanel.nextContactError"))
    }

    setIsLoading(() => false)
  }

  const onConfirmAndGoOffline = async (callAttempted: boolean) => {
    await submitReportAndEndTask(callAttempted)

    if (await setAgentStatus("offline")) {
      send({ type: "GO_OFFLINE" })
    }
  }

  const onConfirmAndGoNext = async (callAttempted: boolean) => {
    await submitReportAndEndTask(callAttempted)

    await leaveCustomPseudoACW()

    send({ type: "GO_NEXT" })
  }

  // We put agents in a custom `PseudoACW` state to make them non-routable to avoid inbound calls.
  // Let's not forget to set them back online after the call report is sent
  async function leaveCustomPseudoACW() {
    invariant(agentRef.current)
    const isInCustomPseudoACW =
      agentRef.current.getStatus().name === CUSTOM_PSEUDO_ACW_STATUS
    if (!isInCustomPseudoACW) return
    await toggleConnectAgentStatus(agentRef.current, "online", {
      enqueueNextState: true,
    })
  }

  return {
    isLoading,
    isError: sendCallReportMutation.isError,
    onConfirmAndGoNext,
    onConfirmAndGoOffline,
    isPseudoACW,
    isVoiceContact,
  }
}

function useCleanUp() {
  const queryClient = useQueryClient()

  const cleanUp = () => {
    // Cleanup local storage
    contactStorageService.removeCallReport()

    // Invalidate queries
    queryClient.invalidateQueries({
      queryKey: ["/case"],
    })
  }

  return cleanUp
}

export { useACWButtonsHook, useCleanUp }
